import React, { useEffect, useMemo } from 'react';
import cx from 'classnames';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RootState } from 'state/store';
import {
  apiFetchScriptProgressV2,
  clearCopyCurrentStepMessage,
  setIsOnScript,
} from 'state/script.state';
import { LoadingStates } from 'typings';
import Zone from 'components/shared/Zone';
import Loader from 'components/Loader';
import ScriptAppointment from './ScriptAppointment';
import ScriptStep from './ScriptStep';
import CopyLeadDetails from './CopyLeadDetails';
import { makeStyles, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';

interface PanelProps {
  className?: string;
}

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
  },
  extraNote: {
    alignItems: 'start',
    display: 'flex',
    fontWeight: 900,
    fontStyle: 'italic',
    fontSize: 'var(--s-1)',
    lineHeight: '1.2rem',
    marginBottom: theme.spacing(1.5),
  },
  extraNoteIcon: {
    marginTop: theme.spacing(0.25),
    marginRight: theme.spacing(0.5),
  },
  scriptSteps: {
    height: '100%',
    overflowX: 'hidden',
    overflowY: 'scroll',
  },
  copyLeadDetails: {
    marginTop: theme.spacing(1.5),
  },
}));

const RightPanelV2 = ({ className }: PanelProps) => {
  const dispatch = useDispatch();
  const styles = useStyles();

  const { leadId, extraNoteForRep } = useSelector(
    (state: RootState) => ({
      leadId: state.lead.entity?.id,
      extraNoteForRep: state.lead.entity?.agent?.extraNoteForRep,
    }),
    shallowEqual
  );

  const currentStep = useSelector((state: RootState) => state.script.currentStep, shallowEqual);

  const scriptSteps = useSelector(
    (state: RootState) => state.script.entityV2?.scriptSteps ?? [],
    shallowEqual
  );

  const { isLoading, updatingStepId, shouldCopy } = useSelector(
    (state: RootState) => ({
      isLoading: state.script.loadingState === LoadingStates.LOADING,
      updatingStepId: state.script.updatingStepId,
      shouldCopy: state.script.shouldCopyCurrentStepMessage,
    }),
    shallowEqual
  );

  // get the appointment URL from the last step that has it
  const appointmentUrl = useMemo(() => {
    for (let i = scriptSteps.length - 1; i >= 0; i--) {
      if (scriptSteps[i].appointmentUrl) {
        return scriptSteps[i].appointmentUrl;
      }
    }

    return null;
  }, [scriptSteps]);

  // when we know the lead ID, fetch the script progress
  useEffect(() => {
    if (leadId) {
      dispatch(apiFetchScriptProgressV2(leadId));
    }
  }, [dispatch, leadId]);

  // copy the message text to the message box
  useEffect(() => {
    if (!shouldCopy || !currentStep) return;

    // there is a listener for this event that places the text in the message box
    document.dispatchEvent(
      new CustomEvent('scriptCopy', {
        detail: {
          // intentionally clearing out the value if we land on a step without a message
          message: currentStep.messageText ?? '',
        },
      })
    );

    // turn the on-script flag on
    dispatch(
      setIsOnScript({
        isOnScript: true,
        onScriptStep: currentStep.name,
      })
    );

    dispatch(clearCopyCurrentStepMessage());
  }, [dispatch, shouldCopy, currentStep]);

  return (
    <Zone
      className={cx('grid-panel', styles.container, className)}
      heading={
        <div className="horizontal-space">
          {isLoading && !updatingStepId && <Loader />}
          <span>script</span>
          {!!appointmentUrl && (
            <ScriptAppointment
              appointmentUrl={appointmentUrl}
              attention={!!currentStep?.appointmentUrl}
            />
          )}
        </div>
      }
    >
      {!!extraNoteForRep && (
        <Typography className={styles.extraNote} variant="caption" color="textPrimary">
          <InfoIcon className={styles.extraNoteIcon} fontSize="inherit" /> {extraNoteForRep}
        </Typography>
      )}
      <div className={styles.scriptSteps}>
        {!!scriptSteps &&
          scriptSteps.map((scriptStep) => (
            <ScriptStep
              key={scriptStep.id}
              isCurrentStep={scriptStep.id === currentStep?.id}
              scriptStep={scriptStep}
            />
          ))}
      </div>
      <div className={styles.copyLeadDetails}>
        <CopyLeadDetails />
      </div>
    </Zone>
  );
};

export default RightPanelV2;
